import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Grid } from '@mui/material';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { removeTimeFromDate } from '../../../helpers/removeTimeFromDate';
import { useGetColumns } from '../hooks/useGetColumns';
import { useGetFilters } from '../hooks/useGetFilters';
import { useGetRows } from '../hooks/useGetRows';
import { SugerenciasMonitoreoFilters } from './SugerenciasMonitoreoFilters';
import { CustomChip } from '../../../components/chip/Chip';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { UPDATE_ESTADO_SUGERENCIA } from '../../../data/graphQL/mutations/AdminSugerencias/actualizarEstadoSugerencias';
import { MonitoreoDialog } from '../../MonitoreoProfesionalesCreation/components/components/TabMonitoreo/MonitoreoDialog';
import { GET_PROFESIONAL_MONITOREOS } from '../../../data/graphQL/queries/Profesionales/getProfesionalMonitoreos';
import { adaptProfesionalMonitoreoCombos, GET_PROFESIONAL_MONITOREO_COMBOS } from '../../../data/graphQL/queries/Profesionales/getProfesionalMonitoreoCombos';
import { GET_RECLAMOS_BY_PROFESIONAL_ID } from '../../../data/graphQL/queries/Reclamos/getGestionReclamoByProfesionalId';
import toast from 'react-hot-toast';

export const AdminSugerenciasMonitoreoContent = ({ setChildrenAction }) => {

  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [isRefetchRequired, setIsRefetchRequired] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [monitoreos, setMonitoreos] = useState([]);
  const [profesionalMonitoreoCombos, setProfesionalMonitoreoCombos] = useState([]);
  const [idProfesional, setIdProfesional] = useState(null);
  const [idProfesionalMonitoreoSelected, setIdProfesionalMonitoreoSelected] = useState(null);
  const [monitoreoSelected, setMonitoreoSelected] = useState(null);
  const [actualizarEstado, { loading: actualizarEstadoLoading }] = useMutation(UPDATE_ESTADO_SUGERENCIA);

  const {
    delegaciones,
    filters,
    handleChangeFilters,
    estadosSugerencias,
    applyClientSideFilters
  } = useGetFilters(setIsRefetchRequired);

  const {
    rows,
    setRows,
    originalData,
    totalCount,
    loading,
    handleSearch
  } = useGetRows(filters, applyClientSideFilters, setIsRefetchRequired);

  const [fetchGestionReclamos, { data: reclamosData }] = useLazyQuery(GET_RECLAMOS_BY_PROFESIONAL_ID, {
    fetchPolicy: 'no-cache'
  });

  const [fetchProfesionalMonitoreos, { data: monitoreosData }] = useLazyQuery(GET_PROFESIONAL_MONITOREOS, {
    fetchPolicy: 'no-cache'
  });

  const { data: dataMonitoreoCombos } = useQuery(GET_PROFESIONAL_MONITOREO_COMBOS, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataMonitoreoCombos) return;
    const combosAdapted = adaptProfesionalMonitoreoCombos(dataMonitoreoCombos.profesionalMonitoreoCombos);
    setProfesionalMonitoreoCombos(combosAdapted);
  }, [dataMonitoreoCombos]);

  const getProfesionalMonitoreo = () => {
    if (idProfesional) {
      fetchProfesionalMonitoreos({
        variables: { idProfesional }
      });
    }
  };

  const getGestionReclamos = () => {
    if (idProfesional) {
      fetchGestionReclamos({
        variables: { idProfesional }
      });
    }
  };

  useEffect(() => {
    getProfesionalMonitoreo();
    getGestionReclamos();
  }, [idProfesional]);

  useEffect(() => {
    setMonitoreoSelected(monitoreos.find((x) => x.idProfesionalMonitoreo === idProfesionalMonitoreoSelected));
  }, [idProfesionalMonitoreoSelected, monitoreos]);

  useEffect(() => {
    if (monitoreosData?.profesionalMonitoreos) {
      setMonitoreos(monitoreosData.profesionalMonitoreos);
    }
  }, [monitoreosData]);

  const getChipEstado = (idEstado) => {
    if (!idEstado) return <CustomChip label={'-'} />;
    const estado = estadosSugerencias.find((e) => e.id === idEstado);
    switch (estado?.codigo?.toUpperCase()) {
      case 'RE':
        return <CustomChip isRed label={estado?.displayName} />;
      case 'PE':
        return <CustomChip isYellow label={estado?.displayName} />;
      case 'AP':
        return <CustomChip isGreen label={estado?.displayName} />;
      default:
        return <CustomChip label={estado?.displayName} />;
    }
  };

  const columns = useGetColumns(delegaciones, getChipEstado, setOpenDialog, setIdProfesional, setIdProfesionalMonitoreoSelected);

  const actualizarEstadoSugerencias = (estado) => {
    const idEstado = estadosSugerencias.find((e) => e.displayName === estado)?.id;
    const idSugerencias = rowSelectionModel.map((index) => rows[index - 1]?.idSugerenciaMonitoreo);

    actualizarEstado({
      variables: {
        idsProfesionalMonitoreoSugerencia: idSugerencias,
        idEstado
      }
    }).then(() => {
      toast.success(`Se ${estado === 'Aprobado' ? rowSelectionModel.length === 1 ? 'Aprobró' : 'Aprobaron' : rowSelectionModel.length === 1 ? 'Rechazó' : 'Rechazaron'} ${rowSelectionModel.length} ${rowSelectionModel.length === 1 ? 'sugerencia' : 'sugerencias'} correctamente`);
      setRowSelectionModel([]);
      handleSearch();
    });
  };

  const estadoActualEsPendiente = useMemo(() => {
    const estadoPendienteId = estadosSugerencias?.find((e) => e.displayName === 'Pendiente')?.id;
    return estadoPendienteId === filters?.idEstado;

  }, [estadosSugerencias, filters?.idEstado]);

  useEffect(() => {
    setChildrenAction(
      <div className='top-buttons'>
        <CustomButton
          className='button-header'
          disabled={!delegaciones?.length || !rowSelectionModel?.length || !estadoActualEsPendiente}
          loading={actualizarEstadoLoading}
          label='Rechazar'
          onClick={() => actualizarEstadoSugerencias('Rechazado')}
          variant='outlined' />

        <CustomButton
          className='button-header'
          disabled={!delegaciones?.length || !rowSelectionModel?.length || !estadoActualEsPendiente}
          loading={actualizarEstadoLoading}
          label='Aprobar'
          onClick={() => actualizarEstadoSugerencias('Aprobado')}
          variant='outlined' />

      </div>
    );
  }, [
    setChildrenAction,
    estadosSugerencias,
    rowSelectionModel,
    actualizarEstadoLoading,
    estadosSugerencias,
    estadoActualEsPendiente]
  );


  const handleChange = (e) => {
    const { value, name } = e.target;

    if (value === filters[name]) return;

    handleChangeFilters(name, value);

    if (name === 'txtFiltro') return;
    setRows([]);
    setIsRefetchRequired(true);
  };

  const handleTextClean = () => {
    handleChangeFilters("txtFiltro", "");
    const data = applyClientSideFilters(originalData, "");
    setRows(data);
  };

  const handleTextFilter = () => {
    const data = applyClientSideFilters(
      originalData,
      filters.txtFiltro
    );
    setRows(data);
  };

  const handleChangeDate = (name, value) => {

    const fechaFormatted = removeTimeFromDate(filters[name]);
    if (value === fechaFormatted) return;

    setRows([]);
    setIsRefetchRequired(true);

    const nuevaFecha = value ? moment(value).format("yyyy-MM-DD") : null;
    handleChangeFilters(name, nuevaFecha);
  };

  const onSelectMonitoreo = (selection) => {
    setRowSelectionModel(selection);
  };

  return (
    <div className='gestion-asistencial-creation-content'>
      <MonitoreoDialog
        open={openDialog}
        profesionalMonitoreoCombos={profesionalMonitoreoCombos}
        handleCloseDialog={() => setOpenDialog(false)}
        readOnly={true}
        monitoreoSelected={monitoreoSelected}
        monitoreos={monitoreos}
        idProfesional={idProfesional}
        reclamos={reclamosData?.gestionReclamoByProfesionalId?.filter((x) => x.idEstadoReclamo === 5) || []} />

      <Grid container spacing={2}>
        <Grid sx={{ width: '100%' }} size={{ xs: 12 }}>
          <SugerenciasMonitoreoFilters
            delegaciones={delegaciones}
            estadosSugerencias={estadosSugerencias}
            handleTextClean={handleTextClean}
            handleTextFilter={handleTextFilter}
            handleSearch={handleSearch}
            handleChange={handleChange}
            handleChangeDate={handleChangeDate}
            filters={filters} />

        </Grid>
        <Grid size={{ xs: 12 }}>
          <DataGridGenerico
            checkboxSelection={estadoActualEsPendiente}
            disabledSelectionAll
            onRowSelectionModelChange={onSelectMonitoreo}
            rowSelectionModel={rowSelectionModel}
            columns={columns}
            totalCount={totalCount}
            rows={rows}
            toolbar={false}
            loading={loading}
            localeText={{
              noRowsLabel: isRefetchRequired ?
                'Haz click en "Buscar" para aplicar los nuevos filtros' :
                "Sin filas"
            }} />
        </Grid>
      </Grid>
    </div>);

};